import {once} from 'lodash';
import React, {
  createContext,
  RefObject,
  useCallback,
  useContext,
  useMemo,
  useRef,
} from 'react';

import {getDatadogRum} from '../../util/datadog/datadogRum';
import {TileMedia, TileMediaSpec, TileMediaTracking} from '../MediaCard';
import {
  defaultState as defaultPanelState,
  MediaBrowserPanelTrackingContextState,
  useMediaBrowserPanelTrackingContext,
  useMediaBrowserPanelTrackingContextMaybe,
} from './MediaBrowserPanelTrackingContext';
import {
  calculateTotalDuration,
  calculateTrackingStages,
} from './utils/tracking';

// Legacy metric name - kept for backward compatibility
const DD_RUM_METRIC_LEGACY = 'mediaBrowserPanelTileTracking';
const DD_RUM_DESCRIPTION = 'Media browser panel tile load duration';

// New per-stage metric names
const DD_RUM_METRIC_STAGE_PREFIX = 'mediaBrowserPanelTile';
const DD_RUM_METRIC_TOTAL = 'mediaBrowserPanelTile.total';

// Feature flag to enable new per-stage vitals (set to true to enable new tracking)
// TODO: Remove legacy tracking once new metrics are validated
const ENABLE_PER_STAGE_VITALS = true;

const EmptyTileMedia: TileMedia = {
  mediaKey: '',
  step: 0,
  index: 0,
  historyRow: {},
  blob: new Blob(),
  objectURL: '',
  filePath: '',
  directURL: '',
  mediaSliderKey: '',
  mediaSliderKeyValue: 0,
};

type MediaBrowserPanelTileTrackingContextType = {
  state: RefObject<MediaBrowserPanelTileTrackingContextState>;
  setAvailableFilesReceivedPerformanceMs: (value: number) => void;
  setDirectUrlReceivedPerformanceMs: (value: number) => void;
  setBlobFetchCompletePerformanceMs: (value: number) => void;
  setDataCachedPerformanceMs: (value: number) => void;
  setMediaLoadedPerformanceMs: (value: number) => void;
  setMediaLoadedSuccess: (value: boolean) => void;
  setTileMedia: (value: TileMedia) => void;
  trackSuccess: (
    tileMedia: TileMedia | null,
    tileMediaSpec: TileMediaSpec | undefined,
    getMediaTracking?: (
      tileMediaSpec: TileMediaSpec
    ) => TileMediaTracking | null
  ) => void;
  trackError: (
    tileMedia: TileMedia | null,
    tileMediaSpec: TileMediaSpec | undefined,
    getMediaTracking?: (
      tileMediaSpec: TileMediaSpec
    ) => TileMediaTracking | null
  ) => void;
  emit: () => void;
  isMediaBrowserPanelTrackingSupported: boolean;
};

export type MediaBrowserPanelTileTrackingContextState = {
  runName: string;
  mediaKey: string;
  componentCreatedMs: number;
  componentCreatedPerformanceMs: number;
  availableFilesReceivedPerformanceMs: number;
  directUrlReceivedPerformanceMs: number;
  blobFetchCompletePerformanceMs: number;
  dataCachedPerformanceMs: number;
  mediaLoadedPerformanceMs: number;
  mediaLoadedSuccess: boolean;
  panel: MediaBrowserPanelTrackingContextState;
  media: {
    dimensions: {
      width: number;
      height: number;
    };
    index: number;
    mime: string;
    path: string;
    size: number;
    step: number;
    stepKey: string;
    stepKeyValue: number;
    type: string;
  };
};

export const MediaBrowserPanelTileTrackingContext =
  createContext<MediaBrowserPanelTileTrackingContextType | null>(null);

export const useMediaBrowserPanelTileTrackingContext = () => {
  const context = useContext(MediaBrowserPanelTileTrackingContext);
  if (!context) {
    throw new Error(
      'useMediaBrowserPanelTileTrackingContext must be used within a MediaBrowserPanelTileTrackingContextProvider'
    );
  }
  return context;
};

export const useMediaBrowserPanelTileTrackingContextMaybe: () => MediaBrowserPanelTileTrackingContextType =
  () => {
    const context = useContext(MediaBrowserPanelTileTrackingContext);
    if (!context) {
      // Fallback object with no-op functions and a dummy ref
      return {
        state: {
          current: {
            runName: '',
            mediaKey: '',
            componentCreatedMs: 0,
            componentCreatedPerformanceMs: 0,
            availableFilesReceivedPerformanceMs: 0,
            directUrlReceivedPerformanceMs: 0,
            blobFetchCompletePerformanceMs: 0,
            dataCachedPerformanceMs: 0,
            mediaLoadedPerformanceMs: 0,
            mediaLoadedSuccess: false,
            panel: defaultPanelState,
            media: {
              dimensions: {width: 0, height: 0},
              index: 0,
              mime: '',
              path: '',
              size: 0,
              step: 0,
              stepKey: '',
              stepKeyValue: 0,
              type: '',
            },
          },
        },
        setAvailableFilesReceivedPerformanceMs: () => {},
        setDirectUrlReceivedPerformanceMs: () => {},
        setBlobFetchCompletePerformanceMs: () => {},
        setDataCachedPerformanceMs: () => {},
        setMediaLoadedPerformanceMs: () => {},
        setMediaLoadedSuccess: () => {},
        setTileMedia: () => {},
        trackSuccess: () => {},
        trackError: () => {},
        emit: () => {},
        isMediaBrowserPanelTrackingSupported: false,
      };
    }
    return context;
  };

export type MediaBrowserPanelTileTrackingContextProviderProps = {
  children: React.ReactNode;
  mediaKey: string;
  panelId: string;
  runName: string;
};

const buildMediaFromTile = (tile: TileMedia, mediaKey: string) => {
  return {
    dimensions: {
      width: tile.historyRow?.[mediaKey]?.width ?? 0,
      height: tile.historyRow?.[mediaKey]?.height ?? 0,
    },
    index: tile.index ?? 0,
    mime: tile.historyRow?.[mediaKey]?.format ?? '',
    path: tile.filePath ?? '',
    size: tile.historyRow?.[mediaKey]?.size ?? 0,
    step: tile.step ?? 0,
    stepKey: tile.mediaSliderKey ?? '',
    stepKeyValue: tile.mediaSliderKeyValue ?? 0,
    type: tile.historyRow?.[mediaKey]?._type ?? '',
  };
};

export const MediaBrowserPanelTileTrackingContextProvider = React.memo(
  ({
    children,
    panelId,
    mediaKey,
    runName,
  }: MediaBrowserPanelTileTrackingContextProviderProps) => {
    const {
      isMediaBrowserPanelTrackingEnabled,
      isMediaBrowserPanelTrackingDebugEnabled,
      isMediaBrowserPanelTrackingSupported,
      state: panelState,
    } = useMediaBrowserPanelTrackingContext();

    // Combine all mutable state into a single ref
    const stateRef = useRef<MediaBrowserPanelTileTrackingContextState>({
      runName,
      mediaKey,
      componentCreatedPerformanceMs: performance.now(),
      componentCreatedMs: Date.now(),
      availableFilesReceivedPerformanceMs: 0,
      directUrlReceivedPerformanceMs: 0,
      blobFetchCompletePerformanceMs: 0,
      dataCachedPerformanceMs: 0,
      mediaLoadedPerformanceMs: 0,
      mediaLoadedSuccess: false,
      panel: {
        ...defaultPanelState,
        ...panelState,
        startTimeMs: panelState.startTimeMs || Date.now(),
      },
      media: buildMediaFromTile(EmptyTileMedia, mediaKey),
    });

    const loggingPrefix = useRef(
      `MediaBrowserPanelTracking ${panelId} tile ${mediaKey}`
    );

    const log = useCallback(
      (event: string, msg: any) => {
        if (isMediaBrowserPanelTrackingDebugEnabled) {
          console.log(loggingPrefix.current, event, JSON.stringify(msg));
        }
      },
      [isMediaBrowserPanelTrackingDebugEnabled]
    );

    const getDuration = useCallback(() => {
      return calculateTotalDuration(stateRef.current);
    }, []);

    const getVital = useCallback(() => {
      // ensure updated panel state prior to building vital
      if (!stateRef.current) {
        return null;
      }
      stateRef.current.panel = panelState;
      const duration = getDuration();
      if (duration === null) {
        return null;
      }

      const stages = calculateTrackingStages(stateRef.current);
      if (!stages) {
        return null;
      }

      const ddContext = {
        ...stateRef.current,
        stages,
      };

      return {
        context: ddContext,
        description: DD_RUM_DESCRIPTION,
        duration,
        startTime: stateRef.current.componentCreatedMs,
      };
    }, [getDuration, panelState]);

    /**
     * Emits legacy single vital with all stages in context.
     * TODO: Remove this function once new per-stage vitals are validated.
     */
    const emitLegacyVital = useCallback(
      (dd_rum: ReturnType<typeof getDatadogRum>, ddContext: any) => {
        dd_rum.addDurationVital(DD_RUM_METRIC_LEGACY, ddContext);
      },
      []
    );

    /**
     * Emits separate vitals for each stage plus a total vital.
     * This provides better queryability and analysis in Datadog.
     *
     * Each stage gets its own vital with correlation context, allowing:
     * - Independent P50/P90/P99 analysis per stage
     * - Stage-specific monitoring and alerts
     * - Filtering and querying by individual stage performance
     */
    const emitPerStageVitals = useCallback(
      (dd_rum: ReturnType<typeof getDatadogRum>, ddContext: any) => {
        const stages = ddContext.context?.stages;
        if (!stages) {
          return;
        }

        // Base context shared across all vitals for correlation
        const baseContext = {
          panelLoadUUID: stateRef.current.panel.panelLoadUUID,
          panelID: stateRef.current.panel.panelID,
          mediaType: stateRef.current.panel.mediaType,
          runName: stateRef.current.runName,
          mediaKey: stateRef.current.mediaKey,
          success: stateRef.current.mediaLoadedSuccess,
          media: stateRef.current.media,
        };

        // Emit a separate vital for each stage
        Object.entries(stages).forEach(([stageName, stage]: [string, any]) => {
          dd_rum.addDurationVital(
            `${DD_RUM_METRIC_STAGE_PREFIX}.${stageName}`,
            {
              startTime: stateRef.current.componentCreatedMs + stage.startTime,
              duration: stage.duration,
              description: `Media tile ${stageName} stage duration`,
              context: {
                ...baseContext,
                stageName,
                stageStartTime: stage.startTime,
              },
            }
          );
        });

        // Also emit total duration for convenience
        dd_rum.addDurationVital(DD_RUM_METRIC_TOTAL, {
          startTime: ddContext.startTime,
          duration: ddContext.duration,
          description: DD_RUM_DESCRIPTION,
          context: {
            ...baseContext,
            stages, // Keep full stage breakdown for reference
          },
        });
      },
      []
    );

    const emit = useCallback(() => {
      if (!isMediaBrowserPanelTrackingEnabled) {
        return;
      }

      const ddContext = getVital();
      // Only emit if we have a valid vital with duration
      if (!ddContext) {
        if (isMediaBrowserPanelTrackingDebugEnabled) {
          log('emit_skipped', 'No valid duration available');
        }
        return;
      }

      try {
        const dd_rum = getDatadogRum();

        // ============================================================
        // LEGACY TRACKING - TODO: Remove after validating new metrics
        // ============================================================
        emitLegacyVital(dd_rum, ddContext);

        // ============================================================
        // NEW PER-STAGE TRACKING
        // ============================================================
        if (ENABLE_PER_STAGE_VITALS) {
          emitPerStageVitals(dd_rum, ddContext);
        }
      } catch (error) {
        console.error('Error adding duration vital', error);
      } finally {
        if (isMediaBrowserPanelTrackingDebugEnabled) {
          log('emit', ddContext);
        }
      }
    }, [
      isMediaBrowserPanelTrackingEnabled,
      isMediaBrowserPanelTrackingDebugEnabled,
      log,
      getVital,
      emitLegacyVital,
      emitPerStageVitals,
    ]);

    const setAvailableFilesReceivedPerformanceMs = useCallback(
      (value: number) => {
        stateRef.current.availableFilesReceivedPerformanceMs = value;
      },
      []
    );

    const setDirectUrlReceivedPerformanceMs = useCallback((value: number) => {
      stateRef.current.directUrlReceivedPerformanceMs = value;
    }, []);

    const setBlobFetchCompletePerformanceMs = useCallback((value: number) => {
      stateRef.current.blobFetchCompletePerformanceMs = value;
    }, []);

    const setDataCachedPerformanceMs = useCallback((value: number) => {
      stateRef.current.dataCachedPerformanceMs = value;
    }, []);

    const setMediaLoadedPerformanceMs = useCallback((value: number) => {
      stateRef.current.mediaLoadedPerformanceMs = value;
    }, []);

    const setMediaLoadedSuccess = useCallback(
      (value: boolean) => {
        stateRef.current.mediaLoadedSuccess = value;
        emit();
      },
      [emit]
    );

    const setTileMedia = useCallback(
      (value: TileMedia) => {
        stateRef.current.media = buildMediaFromTile(value, mediaKey);
      },
      [mediaKey]
    );
    const noOp = useCallback(() => {}, []);

    const tracking = useMediaBrowserPanelTrackingContextMaybe();
    const {incrementCompleted} = tracking;

    const trackCompleted = useCallback(
      (
        success: boolean,
        tileMedia: TileMedia | null,
        tileMediaSpec: TileMediaSpec | undefined,
        getMediaTracking?: (
          tileMediaSpec: TileMediaSpec
        ) => TileMediaTracking | null
      ) => {
        if (!isMediaBrowserPanelTrackingEnabled || tileMedia === null) {
          return;
        }

        setTileMedia(tileMedia);

        // ALWAYS increment panel completion (needed for panel-level tracking)
        // Panel tracks when ALL tiles complete (fresh fetch or cache hit)
        incrementCompleted();

        const tracking = tileMediaSpec
          ? getMediaTracking?.(tileMediaSpec)
          : null;

        if (tracking) {
          const {
            availableFilesReceivedPerformanceMs,
            directUrlReceivedPerformanceMs,
            blobFetchCompletePerformanceMs,
            dataCachedPerformanceMs,
          } = tracking;

          const componentCreated =
            stateRef.current.componentCreatedPerformanceMs;
          /**
           * Tracking cache keys off the tile spec,
           * so if the same spec is loaded multiple times,
           * either by virtualization (scrolling), or by being loaded by multiple panels,
           * we'll already have a cache entry tracking the load performance.
           *
           * We don't want to track a cached load the same way we track a fresh load,
           * but we do want to know how often we're hitting local caches.
           */
          const isCacheHit =
            availableFilesReceivedPerformanceMs < componentCreated ||
            directUrlReceivedPerformanceMs < componentCreated ||
            blobFetchCompletePerformanceMs < componentCreated ||
            dataCachedPerformanceMs < componentCreated;

          if (isMediaBrowserPanelTrackingDebugEnabled) {
            log('trackCompleted', {
              isCacheHit,
              success,
              componentCreated,
              tracking,
            });
          }

          if (isCacheHit) {
            if (isMediaBrowserPanelTrackingDebugEnabled) {
              log('cache_hit_skipped', {
                state: stateRef.current,
                cachedTimestamps: tracking,
              });
            }

            // Track cache hit as an action (count/event) rather than a vital (duration)
            // Actions are appropriate for counting occurrences, not measuring performance
            try {
              const dd_rum = getDatadogRum();
              dd_rum.addAction('mediaBrowserPanelTile.cacheHit', {
                panelLoadUUID: stateRef.current.panel.panelLoadUUID,
                panelID: stateRef.current.panel.panelID,
                mediaType: stateRef.current.panel.mediaType,
                runName: stateRef.current.runName,
                mediaKey: stateRef.current.mediaKey,
                success,
                media: stateRef.current.media,
              });
            } catch (error) {
              console.error('Error tracking cache hit', error);
            }

            return; // Skip tile vital emission for cache hits
          }

          // Fresh fetch - proceed with normal tracking
          setAvailableFilesReceivedPerformanceMs(
            availableFilesReceivedPerformanceMs
          );
          setDirectUrlReceivedPerformanceMs(directUrlReceivedPerformanceMs);
          setBlobFetchCompletePerformanceMs(blobFetchCompletePerformanceMs);
          setDataCachedPerformanceMs(dataCachedPerformanceMs);
        }

        setMediaLoadedPerformanceMs(performance.now());
        setMediaLoadedSuccess(success);
      },
      [
        setTileMedia,
        incrementCompleted,
        isMediaBrowserPanelTrackingEnabled,
        isMediaBrowserPanelTrackingDebugEnabled,
        log,
        setAvailableFilesReceivedPerformanceMs,
        setDirectUrlReceivedPerformanceMs,
        setBlobFetchCompletePerformanceMs,
        setDataCachedPerformanceMs,
        setMediaLoadedPerformanceMs,
        setMediaLoadedSuccess,
      ]
    );
    const trackSuccess = useCallback(
      (
        tileMedia: TileMedia | null,
        tileMediaSpec: TileMediaSpec | undefined,
        getMediaTracking?: (
          tileMediaSpec: TileMediaSpec
        ) => TileMediaTracking | null
      ) => trackCompleted(true, tileMedia, tileMediaSpec, getMediaTracking),
      [trackCompleted]
    );
    const trackError = useCallback(
      (
        tileMedia: TileMedia | null,
        tileMediaSpec: TileMediaSpec | undefined,
        getMediaTracking?: (
          tileMediaSpec: TileMediaSpec
        ) => TileMediaTracking | null
      ) => trackCompleted(false, tileMedia, tileMediaSpec, getMediaTracking),
      [trackCompleted]
    );

    const contextValue = React.useMemo(
      () => ({
        state: stateRef,
        setAvailableFilesReceivedPerformanceMs:
          isMediaBrowserPanelTrackingSupported
            ? once(setAvailableFilesReceivedPerformanceMs)
            : noOp,
        setDirectUrlReceivedPerformanceMs: isMediaBrowserPanelTrackingSupported
          ? once(setDirectUrlReceivedPerformanceMs)
          : noOp,
        setBlobFetchCompletePerformanceMs: isMediaBrowserPanelTrackingSupported
          ? once(setBlobFetchCompletePerformanceMs)
          : noOp,
        setDataCachedPerformanceMs: isMediaBrowserPanelTrackingSupported
          ? once(setDataCachedPerformanceMs)
          : noOp,
        setMediaLoadedPerformanceMs: isMediaBrowserPanelTrackingSupported
          ? once(setMediaLoadedPerformanceMs)
          : noOp,
        setMediaLoadedSuccess: isMediaBrowserPanelTrackingSupported
          ? once(setMediaLoadedSuccess)
          : noOp,
        setTileMedia: isMediaBrowserPanelTrackingSupported
          ? once(setTileMedia)
          : noOp,
        trackSuccess: isMediaBrowserPanelTrackingSupported
          ? once(trackSuccess)
          : noOp,
        trackError: isMediaBrowserPanelTrackingSupported
          ? once(trackError)
          : noOp,
        emit,
        isMediaBrowserPanelTrackingSupported,
      }),
      [
        isMediaBrowserPanelTrackingSupported,
        setAvailableFilesReceivedPerformanceMs,
        setDirectUrlReceivedPerformanceMs,
        setBlobFetchCompletePerformanceMs,
        setDataCachedPerformanceMs,
        setMediaLoadedPerformanceMs,
        setMediaLoadedSuccess,
        setTileMedia,
        trackSuccess,
        trackError,
        emit,
        noOp,
      ]
    );

    return (
      <MediaBrowserPanelTileTrackingContext.Provider value={contextValue}>
        {children}
      </MediaBrowserPanelTileTrackingContext.Provider>
    );
  }
);

export function useGetTrackingCallbacks(
  tileMedia: TileMedia | null,
  tileMediaSpec: TileMediaSpec | undefined,
  getMediaTracking?: (tileMediaSpec: TileMediaSpec) => TileMediaTracking | null
) {
  const {trackSuccess, trackError} =
    useMediaBrowserPanelTileTrackingContextMaybe();
  const trackSuccessCallback = useCallback(() => {
    return trackSuccess(tileMedia, tileMediaSpec, getMediaTracking);
  }, [trackSuccess, tileMedia, tileMediaSpec, getMediaTracking]);
  const trackErrorCallback = useCallback(() => {
    return trackError(tileMedia, tileMediaSpec, getMediaTracking);
  }, [trackError, tileMedia, tileMediaSpec, getMediaTracking]);
  return useMemo(
    () => ({
      trackSuccess: trackSuccessCallback,
      trackError: trackErrorCallback,
    }),
    [trackSuccessCallback, trackErrorCallback]
  );
}
